import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useWagmiWeb3 } from './WagmiWeb3Context';
import { useTron } from './TronContext';

const UnifiedWalletContext = createContext();

export const useUnifiedWallet = () => {
  const context = useContext(UnifiedWalletContext);
  if (!context) {
    throw new Error('useUnifiedWallet must be used within a UnifiedWalletProvider');
  }
  return context;
};

// Supported wallet types
export const WALLET_TYPES = {
  METAMASK: 'metamask',
  TRONLINK: 'tronlink',
};

// Supported chain types
export const CHAIN_TYPES = {
  EVM: 'evm',
  TRON: 'tron',
};

const ACTIVE_WALLET_STORAGE_KEY = 'activeWalletType';

const readStoredActiveWallet = () => {
  if (typeof window === 'undefined') return null;
  try {
    const v = localStorage.getItem(ACTIVE_WALLET_STORAGE_KEY);
    if (v === 'metamask' || v === 'tronlink') return v;
  } catch {
    // ignore
  }
  return null;
};

export const UnifiedWalletProvider = ({ children }) => {
  // User's explicit choice — persisted so reload is deterministic when both
  // wagmi and TronLink auto-reconnect at startup.
  const [preferredWallet, setPreferredWallet] = useState(readStoredActiveWallet);

  // EVM wallet context (MetaMask via Wagmi)
  const evmWallet = useWagmiWeb3();

  // Tron wallet context (TronLink)
  const tronWallet = useTron();

  const evmConnected = !!evmWallet.isConnected;
  const tronConnected = !!tronWallet.account;

  // EVM and TRON are different chains and coexist freely. preferredWallet is
  // the user's most recent focus (drives single-wallet UI), not a kill switch.
  const { activeWallet, activeChainType } = useMemo(() => {
    if (preferredWallet === WALLET_TYPES.METAMASK && evmConnected) {
      return { activeWallet: WALLET_TYPES.METAMASK, activeChainType: CHAIN_TYPES.EVM };
    }
    if (preferredWallet === WALLET_TYPES.TRONLINK && tronConnected) {
      return { activeWallet: WALLET_TYPES.TRONLINK, activeChainType: CHAIN_TYPES.TRON };
    }
    if (evmConnected && !tronConnected) {
      return { activeWallet: WALLET_TYPES.METAMASK, activeChainType: CHAIN_TYPES.EVM };
    }
    if (tronConnected && !evmConnected) {
      return { activeWallet: WALLET_TYPES.TRONLINK, activeChainType: CHAIN_TYPES.TRON };
    }
    if (evmConnected && tronConnected) {
      return { activeWallet: WALLET_TYPES.METAMASK, activeChainType: CHAIN_TYPES.EVM };
    }
    return { activeWallet: null, activeChainType: null };
  }, [preferredWallet, evmConnected, tronConnected]);

  // Keep the persisted preference in sync — if the active wallet shifts (e.g.
  // user disconnects via the wallet extension itself), record the new state.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (activeWallet) {
        localStorage.setItem(ACTIVE_WALLET_STORAGE_KEY, activeWallet);
      } else if (!evmConnected && !tronConnected) {
        localStorage.removeItem(ACTIVE_WALLET_STORAGE_KEY);
      }
    } catch {
      // ignore quota/disabled-storage errors
    }
  }, [activeWallet, evmConnected, tronConnected]);

  // Get current wallet info
  const getCurrentWallet = useMemo(() => {
    switch (activeWallet) {
      case WALLET_TYPES.METAMASK:
        return {
          type: WALLET_TYPES.METAMASK,
          chainType: CHAIN_TYPES.EVM,
          account: evmWallet.account,
          network: evmWallet.network,
          isNetworkSupported: evmWallet.isNetworkSupported,
          error: evmWallet.error,
        };
      case WALLET_TYPES.TRONLINK:
        return {
          type: WALLET_TYPES.TRONLINK,
          chainType: CHAIN_TYPES.TRON,
          account: tronWallet.account,
          network: tronWallet.network,
          isNetworkSupported: tronWallet.isNetworkSupported,
          tronWeb: tronWallet.tronWeb,
          error: tronWallet.error,
        };
      default:
        return null;
    }
  }, [activeWallet, evmWallet.account, evmWallet.network, evmWallet.isNetworkSupported, evmWallet.error, tronWallet.account, tronWallet.network, tronWallet.isNetworkSupported, tronWallet.tronWeb, tronWallet.error]);

  // Single-active-wallet: connecting one tears down the other so the user
  // never sees two pills, and there's no ambiguity about which wallet drives
  // the next tx.
  const connectWallet = useCallback(async (walletType) => {
    switch (walletType) {
      case WALLET_TYPES.METAMASK:
        if (tronConnected) tronWallet.disconnectTronLink();
        setPreferredWallet(WALLET_TYPES.METAMASK);
        return await evmWallet.connectWallet();
      case WALLET_TYPES.TRONLINK:
        if (evmConnected) evmWallet.disconnectWallet();
        setPreferredWallet(WALLET_TYPES.TRONLINK);
        return await tronWallet.connectTronLink();
      default:
        throw new Error(`Unsupported wallet type: ${walletType}`);
    }
  }, [evmWallet, tronWallet, evmConnected, tronConnected]);

  // Auto-reconnect runs independently for wagmi and tronlink, so on reload
  // the "wrong" wallet can come back to life. Tear down whichever doesn't
  // match the user's last explicit choice.
  useEffect(() => {
    if (!preferredWallet) return;
    if (preferredWallet === WALLET_TYPES.METAMASK && tronConnected) {
      tronWallet.disconnectTronLink();
    } else if (preferredWallet === WALLET_TYPES.TRONLINK && evmConnected) {
      evmWallet.disconnectWallet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferredWallet, evmConnected, tronConnected]);

  // Disconnect — tears down whichever wallet(s) are alive so no listeners or
  // stored sessions are left behind, and clears the persisted preference.
  const disconnectWallet = useCallback(() => {
    if (evmConnected) evmWallet.disconnectWallet();
    if (tronConnected) tronWallet.disconnectTronLink();
    setPreferredWallet(null);
  }, [evmConnected, tronConnected, evmWallet, tronWallet]);

  // Disconnect just one chain — used by the dual-pill header so each wallet
  // can be torn down independently without nuking the other.
  const disconnectChain = useCallback((chainType) => {
    if (chainType === CHAIN_TYPES.EVM) {
      evmWallet.disconnectWallet();
      if (preferredWallet === WALLET_TYPES.METAMASK) {
        setPreferredWallet(tronConnected ? WALLET_TYPES.TRONLINK : null);
      }
    } else if (chainType === CHAIN_TYPES.TRON) {
      tronWallet.disconnectTronLink();
      if (preferredWallet === WALLET_TYPES.TRONLINK) {
        setPreferredWallet(evmConnected ? WALLET_TYPES.METAMASK : null);
      }
    }
  }, [evmWallet, tronWallet, preferredWallet, evmConnected, tronConnected]);

  // Resolve the right wallet for a target chain — for components that pick
  // the chain from form state rather than from the global focus wallet.
  const walletForChain = useCallback((chainType) => {
    if (chainType === CHAIN_TYPES.EVM) {
      return evmConnected ? {
        type: WALLET_TYPES.METAMASK,
        chainType: CHAIN_TYPES.EVM,
        account: evmWallet.account,
        network: evmWallet.network,
        isNetworkSupported: evmWallet.isNetworkSupported,
      } : null;
    }
    if (chainType === CHAIN_TYPES.TRON) {
      return tronConnected ? {
        type: WALLET_TYPES.TRONLINK,
        chainType: CHAIN_TYPES.TRON,
        account: tronWallet.account,
        network: tronWallet.network,
        isNetworkSupported: tronWallet.isNetworkSupported,
        tronWeb: tronWallet.tronWeb,
      } : null;
    }
    return null;
  }, [evmConnected, tronConnected, evmWallet, tronWallet]);

  // Check if any wallet is connected
  const isConnected = useMemo(() => {
    return !!(evmWallet.isConnected || tronWallet.account);
  }, [evmWallet.isConnected, tronWallet.account]);

  // Check if current network is supported
  const isNetworkSupported = useMemo(() => {
    if (activeWallet === WALLET_TYPES.METAMASK) {
      return evmWallet.isNetworkSupported;
    } else if (activeWallet === WALLET_TYPES.TRONLINK) {
      return tronWallet.isNetworkSupported;
    }
    return false;
  }, [activeWallet, evmWallet.isNetworkSupported, tronWallet.isNetworkSupported]);

  // Get available wallets with their status
  const getAvailableWallets = useCallback(() => {
    return [
      {
        type: WALLET_TYPES.METAMASK,
        name: 'MetaMask',
        chainType: CHAIN_TYPES.EVM,
        icon: '🦊',
        isInstalled: evmWallet.isMetaMaskInstalled(),
        isConnected: evmWallet.isConnected,
        account: evmWallet.account,
        network: evmWallet.network,
        isNetworkSupported: evmWallet.isNetworkSupported,
        supportedNetworks: evmWallet.supportedNetworks,
      },
      {
        type: WALLET_TYPES.TRONLINK,
        name: 'TronLink',
        chainType: CHAIN_TYPES.TRON,
        icon: '🔷',
        isInstalled: tronWallet.isTronLinkInstalled(),
        isConnected: !!tronWallet.account,
        account: tronWallet.account,
        network: tronWallet.network,
        isNetworkSupported: tronWallet.isNetworkSupported,
        supportedNetworks: tronWallet.supportedNetworks,
      },
    ];
  }, [evmWallet, tronWallet]);

  // Format address for display
  const formatAddress = useCallback((address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }, []);

  // Get wallet-specific transaction methods
  const getTransactionMethods = useCallback(() => {
    const currentWallet = getCurrentWallet;
    if (!currentWallet) return null;

    if (currentWallet.chainType === CHAIN_TYPES.EVM) {
      return {
        sendTransaction: async (txData) => {
          return await currentWallet.signer.sendTransaction(txData);
        },
        getBalance: async (address) => {
          return await currentWallet.provider.getBalance(address || currentWallet.account);
        },
      };
    } else if (currentWallet.chainType === CHAIN_TYPES.TRON) {
      return {
        sendTransaction: async (txData) => {
          return await currentWallet.tronWeb.trx.sendTransaction(txData);
        },
        getBalance: async (address) => {
          return await tronWallet.getBalance(address);
        },
      };
    }

    return null;
  }, [getCurrentWallet, tronWallet]);

  const value = useMemo(() => ({
    // Current wallet state
    activeWallet,
    activeChainType,
    isConnected,
    isNetworkSupported,
    currentWallet: getCurrentWallet,

    // Wallet management
    connectWallet,
    disconnectWallet,
    disconnectChain,
    walletForChain,
    getAvailableWallets,
    formatAddress,
    getTransactionMethods,

    // Direct access to specific wallet contexts
    evmWallet,
    tronWallet,

    // Constants
    WALLET_TYPES,
    CHAIN_TYPES,
  }), [activeWallet, activeChainType, isConnected, isNetworkSupported, getCurrentWallet, connectWallet, disconnectWallet, disconnectChain, walletForChain, getAvailableWallets, formatAddress, getTransactionMethods, evmWallet, tronWallet]);

  return (
    <UnifiedWalletContext.Provider value={value}>
      {children}
    </UnifiedWalletContext.Provider>
  );
};